"use client";

import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CartQuantityStepper } from "@/components/blocks/CartQuantityStepper";

export type ProductCardVariant = "default" | "storefront";

export interface QuantityAddToCartProps {
  name: string;
  quantity: number;
  maxQuantity?: number;
  onQuantityChange?: (value: string) => void;
  onAddToCart?: () => void;
  isPending?: boolean;
  addToCartLabel: string;
  cartQuantity?: number;
  onCartQuantityChange?: (quantity: number) => void;
  isCartUpdating?: boolean;
  variant: ProductCardVariant;
}

/**
 * Combined quantity picker + Add to Cart control. Before the product is in
 * the cart, +/- and the number input adjust a local pending quantity and
 * "Add to Cart" commits it. Once the product is in the cart, +/- and the
 * number input adjust the cart quantity directly instead.
 */
export function QuantityAddToCart({
  name,
  quantity,
  maxQuantity,
  onQuantityChange,
  onAddToCart,
  isPending,
  addToCartLabel,
  cartQuantity,
  onCartQuantityChange,
  isCartUpdating,
  variant,
}: QuantityAddToCartProps) {
  const inCart = (cartQuantity ?? 0) > 0 && !!onCartQuantityChange;
  const displayQuantity = inCart ? (cartQuantity ?? 0) : quantity;
  const busy = Boolean(isPending || isCartUpdating);
  const isStorefront = variant === "storefront";
  const stepperHeight = isStorefront ? "h-11" : "h-10";

  function handleStepperChange(next: number) {
    if (inCart) {
      onCartQuantityChange?.(next);
    } else {
      onQuantityChange?.(String(next));
    }
  }

  return (
    <div className="flex items-center gap-2">
      <CartQuantityStepper
        name={name}
        quantity={displayQuantity}
        maxQuantity={maxQuantity}
        onChange={handleStepperChange}
        disabled={busy}
        className={stepperHeight}
      />

      <Button
        type="button"
        variant={isStorefront ? "outline" : "default"}
        className={cn(
          "flex-1",
          stepperHeight,
          isStorefront &&
            "rounded-xl bg-card text-foreground shadow-sm hover:bg-primary/10",
        )}
        onClick={onAddToCart}
        disabled={!onAddToCart || busy || inCart}
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : inCart ? (
          <Check className="h-4 w-4" />
        ) : null}
        {busy ? "Adding..." : inCart ? "In Cart" : addToCartLabel}
      </Button>
    </div>
  );
}
